import { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ListChecks, Gavel, Plus, Repeat, Trash2, FileText } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import {
  MEETING_STATUS,
  MEETING_TYPE,
  localInputToIso,
  meetingsKey,
  shiftMonth,
  useCadences,
  useCalendar,
  useMeetings,
  type Cadence,
  type MeetingType,
} from '@/lib/meetings';
import { todayIn } from '@/lib/reports';
import type { Program } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

const TYPE_DOT: Record<MeetingType, string> = {
  STEERING_COMMITTEE: 'bg-brand',
  WORKING_SESSION: 'bg-info',
  VENDOR_DEMONSTRATION: 'bg-warn',
};
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function MeetingsPage() {
  const program = useOutletContext<Program>();
  const canManage = program.capabilities.includes('plan.manage');
  const [month, setMonth] = useState(todayIn(program.timezone).slice(0, 7));
  const [creating, setCreating] = useState(false);
  const [cadence, setCadence] = useState(false);
  const list = useMeetings(program.id);
  const today = todayIn(program.timezone);

  const upcoming = (list.data ?? []).filter((m) => m.status === 'SCHEDULED').reverse();
  const past = (list.data ?? []).filter((m) => m.status !== 'SCHEDULED');

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <PageHeader
        title="Meetings"
        description="Steering Committee, working sessions and vendor demonstrations. Action items become tasks; decisions go to the RAID log."
        actions={
          canManage && (
            <>
              <Button variant="secondary" onClick={() => setCadence(true)}>
                <Repeat /> Cadence
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus /> New meeting
              </Button>
            </>
          )
        }
      />
      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <MonthCalendar program={program} month={month} setMonth={setMonth} today={today} />
        <div className="space-y-5">
          <MeetingList program={program} title="Upcoming" rows={upcoming} empty="Nothing scheduled." />
          <MeetingList program={program} title="Recent" rows={past.slice(0, 12)} empty="No meetings recorded yet." />
        </div>
      </div>
      {creating && <NewMeeting program={program} onClose={() => setCreating(false)} />}
      {cadence && <CadenceDialog program={program} onClose={() => setCadence(false)} />}
    </div>
  );
}

function MonthCalendar({
  program,
  month,
  setMonth,
  today,
}: {
  program: Program;
  month: string;
  setMonth: (m: string) => void;
  today: string;
}) {
  const cal = useCalendar(program.id, month);
  const first = new Date(`${month}-01T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells: Array<string | null> = [
    ...Array(lead).fill(null),
    ...Array.from({ length: days }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ];
  const missed = cal.data?.expected.filter((e) => e.missed) ?? [];

  return (
    <Card className="p-0">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          <ChevronLeft />
        </Button>
        <h2 className="min-w-36 text-center text-sm font-semibold">
          {first.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
        </h2>
        <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
          <ChevronRight />
        </Button>
        {missed.length > 0 && (
          <Badge tone="bad" className="ml-auto">
            {missed.length} expected meeting{missed.length === 1 ? '' : 's'} missed
          </Badge>
        )}
      </div>
      {!cal.data ? (
        <Spinner className="h-64" />
      ) : (
        <div className="grid grid-cols-7 gap-px bg-border text-[12px]">
          {WEEKDAYS.map((d) => (
            <div key={d} className="bg-surface-2 px-2 py-1 text-center text-[11px] font-medium text-muted">
              {d}
            </div>
          ))}
          {cells.map((d, i) => {
            const meetings = d ? cal.data.meetings.filter((m) => m.date === d) : [];
            const expected = d ? cal.data.expected.filter((e) => e.date === d && !e.met) : [];
            return (
              <div key={i} className={cn('min-h-[84px] bg-surface p-1.5', !d && 'bg-surface-2/40')}>
                {d && (
                  <>
                    <span className={cn('text-[11px] text-muted', d === today && 'rounded bg-brand px-1 text-white')}>
                      {Number(d.slice(8))}
                    </span>
                    <div className="mt-1 space-y-1">
                      {meetings.map((m) => (
                        <Link
                          key={m.id}
                          to={m.id}
                          className={cn(
                            'flex items-center gap-1 truncate rounded px-1 py-0.5 hover:bg-surface-2',
                            m.status === 'CANCELLED' && 'text-muted line-through',
                          )}
                          title={`${MEETING_TYPE[m.type]}: ${m.title}`}
                        >
                          <span className={cn('size-1.5 shrink-0 rounded-full', TYPE_DOT[m.type])} />
                          <span className="truncate">{m.title}</span>
                        </Link>
                      ))}
                      {expected.map((e) => (
                        <div
                          key={e.cadenceId}
                          className={cn(
                            'truncate rounded border border-dashed px-1 py-0.5',
                            e.missed ? 'border-bad/50 text-bad' : 'border-border-strong text-muted',
                          )}
                          title={e.missed ? `${e.title} expected, not held` : `${e.title} expected`}
                        >
                          {e.missed ? 'Missed: ' : ''}
                          {e.title}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function MeetingList({
  program,
  title,
  rows,
  empty,
}: {
  program: Program;
  title: string;
  rows: ReturnType<typeof useMeetings>['data'];
  empty: string;
}) {
  return (
    <Card className="p-0">
      <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">{title}</h2>
      {!rows?.length ? (
        <p className="px-4 py-3 text-sm text-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((m) => (
            <li key={m.id}>
              <Link to={`/p/${program.id}/meetings/${m.id}`} className="block px-4 py-2.5 hover:bg-surface-2/60">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className={cn('size-2 shrink-0 rounded-full', TYPE_DOT[m.type])} />
                  <span className="flex-1 truncate font-medium">{m.title}</span>
                  <Badge tone={MEETING_STATUS[m.status].tone}>{MEETING_STATUS[m.status].label}</Badge>
                </div>
                <div className="mt-0.5 flex items-center gap-3 pl-4 text-xs text-muted">
                  <span>{fmtDateTime(m.heldAt)}</span>
                  {m.actionItems > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <ListChecks className="size-3" /> {m.actionItems}
                    </span>
                  )}
                  {m.decisions > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <Gavel className="size-3" /> {m.decisions}
                    </span>
                  )}
                  {m.hasMinutes && (
                    <span className="inline-flex items-center gap-0.5">
                      <FileText className="size-3" /> minutes
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function NewMeeting({ program, onClose }: { program: Program; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [f, setF] = useState({ type: 'STEERING_COMMITTEE' as MeetingType, title: '', heldAt: '', agenda: '' });
  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>(`/api/programs/${program.id}/meetings`, {
        type: f.type,
        title: f.title,
        heldAt: localInputToIso(f.heldAt),
        agenda: f.agenda || null,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: meetingsKey(program.id) });
      onClose();
      nav(`/p/${program.id}/meetings/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="New meeting"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!f.title.trim() || !f.heldAt}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Type" htmlFor="mt-type">
          <Select id="mt-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as MeetingType })}>
            {(Object.keys(MEETING_TYPE) as MeetingType[]).map((t) => (
              <option key={t} value={t}>
                {MEETING_TYPE[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title" htmlFor="mt-title">
          <Input
            id="mt-title"
            value={f.title}
            maxLength={200}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label="Date and time" htmlFor="mt-when">
          <Input
            id="mt-when"
            type="datetime-local"
            value={f.heldAt}
            onChange={(e) => setF({ ...f, heldAt: e.target.value })}
          />
        </Field>
        <Field label="Agenda" htmlFor="mt-agenda" hint="Optional. Markdown is fine.">
          <Textarea id="mt-agenda" rows={4} value={f.agenda} onChange={(e) => setF({ ...f, agenda: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}

const DOWS = [
  ['MON', 'Monday'],
  ['TUE', 'Tuesday'],
  ['WED', 'Wednesday'],
  ['THU', 'Thursday'],
  ['FRI', 'Friday'],
] as const;
const NTHS = [
  ['1', 'First'],
  ['2', 'Second'],
  ['3', 'Third'],
  ['4', 'Fourth'],
  ['L', 'Last'],
] as const;

function CadenceDialog({ program, onClose }: { program: Program; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const existing = useCadences(program.id);
  const [rows, setRows] = useState<Cadence[] | null>(null);
  const list = rows ?? existing.data ?? [];
  const set = (i: number, p: Partial<Cadence>) => setRows(list.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const save = useMutation({
    mutationFn: () =>
      api.put(
        `/api/programs/${program.id}/meeting-cadences`,
        list.map(({ type, title, frequency, rule }) => ({ type, title, frequency, rule })),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingsKey(program.id) });
      toast.ok('Cadence saved.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title="Meeting cadence"
      description="The rhythm the calendar expects. A past expected meeting with nothing held near it shows as missed."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      {!existing.data ? (
        <Spinner className="h-24" />
      ) : (
        <div className="space-y-2">
          {list.map((r, i) => {
            const [n, d] = r.frequency === 'WEEKLY' ? ['1', r.rule] : r.rule.split(':');
            const rule = (nn: string, dd: string) => (r.frequency === 'WEEKLY' ? dd : `${nn}:${dd}`);
            return (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  value={r.title}
                  onChange={(e) => set(i, { title: e.target.value })}
                  className="w-56"
                  aria-label="Cadence title"
                />
                <Select
                  value={r.type}
                  onChange={(e) => set(i, { type: e.target.value as MeetingType })}
                  className="w-48"
                  aria-label="Meeting type"
                >
                  {(Object.keys(MEETING_TYPE) as MeetingType[]).map((t) => (
                    <option key={t} value={t}>
                      {MEETING_TYPE[t]}
                    </option>
                  ))}
                </Select>
                <Select
                  value={r.frequency}
                  onChange={(e) => {
                    const f = e.target.value as Cadence['frequency'];
                    set(i, { frequency: f, rule: f === 'WEEKLY' ? (d ?? 'TUE') : `${n ?? '1'}:${d ?? 'TUE'}` });
                  }}
                  className="w-32"
                  aria-label="Frequency"
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                </Select>
                {r.frequency !== 'WEEKLY' && (
                  <Select
                    value={n}
                    onChange={(e) => set(i, { rule: rule(e.target.value, d!) })}
                    className="w-28"
                    aria-label="Which week"
                  >
                    {NTHS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                )}
                <Select
                  value={d}
                  onChange={(e) => set(i, { rule: rule(n!, e.target.value) })}
                  className="w-32"
                  aria-label="Day"
                >
                  {DOWS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remove"
                  onClick={() => setRows(list.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setRows([
                ...list,
                { type: 'STEERING_COMMITTEE', title: 'Steering Committee', frequency: 'MONTHLY', rule: '1:THU' },
              ])
            }
          >
            <Plus /> Add a cadence
          </Button>
        </div>
      )}
    </Dialog>
  );
}
